import { ReactNode, useId } from 'react';

import { arteDoSelo, criterioDoSelo, nomeDoTipo, TipoDeDestaque, tituloDoTipo } from '../destaques-modelo';

interface SecaoDoSeloProps {
  tipo: TipoDeDestaque;
  /** Título do ciclo ("1S2026"). */
  ciclo: string;
  comportamento?: string | null;
  /** Ao lado do título (o (i) das regras da Cultura). */
  aoLadoDoTitulo?: ReactNode;
  children: ReactNode;
}

/**
 * Seção de um selo conquistado no perfil (`Galeria.dc.html`, DST-22, DST-23, DST-26): a faixa com a arte, o
 * título, o critério e "Selo conquistado", e abaixo o que levou ao selo.
 */
export default function SecaoDoSelo({ tipo, ciclo, comportamento, aoLadoDoTitulo, children }: SecaoDoSeloProps) {
  const tituloId = useId();
  const arte = arteDoSelo(tipo, ciclo, comportamento);

  return (
    <section
      aria-labelledby={tituloId}
      className="flex flex-col gap-[18px] rounded-3xl bg-surface-0 px-[26px] pb-7 pt-6 shadow-jj-01"
    >
      <div className="flex flex-wrap items-center gap-[22px] rounded-2xl bg-surface-100 px-6 py-[18px]">
        {arte && (
          <img
            src={arte}
            alt={`Selo ${nomeDoTipo(tipo, comportamento)}`}
            className="h-[104px] w-[104px] shrink-0 object-contain drop-shadow-md"
          />
        )}
        <div className="relative flex min-w-[220px] flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <h2 id={tituloId} className="m-0 font-display text-[22px] font-bold leading-tight text-brand-blue-dark">
              {tituloDoTipo(tipo)}
            </h2>
            {aoLadoDoTitulo}
          </div>
          <p className="m-0 max-w-[60ch] text-sm leading-normal text-content">{criterioDoSelo(tipo, ciclo, comportamento)}</p>
        </div>
        <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-jj-pill bg-brand-yellow px-3.5 py-[7px] font-display text-xs font-bold text-brand-blue-dark">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          Selo conquistado
        </span>
      </div>
      {children}
    </section>
  );
}
